import React, { useState } from "react";
import { Check, RefreshCw, Search, SlidersHorizontal, X } from "lucide-react";

import FormButton from "../../../components/form/FormButton";
import FormInput from "../../../components/form/FormInput";
import Paginator from "../../../components/table/Paginator";

const formatNumber = (value) => {
    const n = Number(value || 0);
    return n.toLocaleString("en-IN");
};

const formatPercent = (value) => {
    const n = Number(value || 0);
    return `${n.toFixed(1)}%`;
};

const AnalyticsCard = ({ label, value, note }) => (
    <article className="lg-analytics-card">
        <span>{label}</span>
        <strong>{value}</strong>
        {note ? <small>{note}</small> : null}
    </article>
);

const MiniBar = ({ label, value, max }) => {
    const width = max > 0 ? Math.max((Number(value || 0) / max) * 100, 4) : 0;

    return (
        <div className="lg-analytics-bar-row">
            <div className="lg-analytics-bar-label">
                <span>{label}</span>
                <strong>{formatNumber(value)}</strong>
            </div>
            <div className="lg-analytics-bar-track">
                <div
                    className="lg-analytics-bar-fill"
                    style={{ width: `${width}%` }}
                />
            </div>
        </div>
    );
};

const LocalGameAnalyticsPanel = ({
    data,
    loading,
    onClose,
    onRefresh,
    selectedFilter = "all",
    onFilterChange,
    selectedStatus = "all",
    onStatusChange,

        customVenues = [],
    customVenuesLoading = false,
    customVenuesMeta = {},
    customVenuesSearch = "",
    onCustomVenuesSearchChange,
    onCustomVenueSearchSubmit,
    onCustomVenueRefresh,
    onCustomVenuePage,
    onCustomVenuePageSize,
}) => {
    const metrics = data?.metrics || {};
    const topSports = Array.isArray(data?.topSports) ? data.topSports : [];
    const dailyTrend = Array.isArray(data?.dailyTrend) ? data.dailyTrend : [];
    const venueTypeBreakdown = Array.isArray(data?.venueTypeBreakdown)
        ? data.venueTypeBreakdown
        : [];
    const statusBreakdown = Array.isArray(data?.statusBreakdown)
        ? data.statusBreakdown
        : [];

        const findPlayersNotifications = data?.findPlayersNotifications || {};
const findPlayersSummary = findPlayersNotifications.summary || {};
const findPlayersReasonBreakdown = Array.isArray(
    findPlayersNotifications.reasonBreakdown
)
    ? findPlayersNotifications.reasonBreakdown
    : [];
const findPlayersRecentBatches = Array.isArray(
    findPlayersNotifications.recentBatches
)
    ? findPlayersNotifications.recentBatches
    : [];
const findPlayersRecentUsers = Array.isArray(
    findPlayersNotifications.recentUsers
)
    ? findPlayersNotifications.recentUsers
    : [];

    const filterOptions = [
        { label: "All time", value: "all" },
        { label: "Today", value: "today" },
        { label: "This week", value: "thisWeek" },
        { label: "This month", value: "thisMonth" },
    ];

    const statusOptions = [
        { label: "All status", value: "all" },
        { label: "Upcoming", value: "upcoming" },
        { label: "Ongoing", value: "ongoing" },
        { label: "Completed", value: "completed" },
        { label: "Cancelled", value: "cancelled" },
    ];

    const activeHosts = Array.isArray(data?.activeHosts) ? data.activeHosts : [];
    const activeJoiningPlayers = Array.isArray(data?.activeJoiningPlayers)
        ? data.activeJoiningPlayers
        : [];

    const maxSportPlayers = Math.max(
        ...topSports.map((item) => Number(item.players || 0)),
        0
    );

    const maxTrendGames = Math.max(
        ...dailyTrend.map((item) => Number(item.games || 0)),
        0
    );

    const maxVenueType = Math.max(
        ...venueTypeBreakdown.map((item) => Number(item.value || 0)),
        0
    );

    const maxStatus = Math.max(
        ...statusBreakdown.map((item) => Number(item.value || 0)),
        0
    );

    const maxFindPlayersReason = Math.max(
    ...findPlayersReasonBreakdown.map((item) => Number(item.value || 0)),
    0
);

    const [filterOpen, setFilterOpen] = useState(false);

    const selectedTimeLabel =
        filterOptions.find((item) => item.value === selectedFilter)?.label ||
        "All time";

    const selectedStatusLabel =
        statusOptions.find((item) => item.value === selectedStatus)?.label ||
        "All status";

            const customVenuesPage = Number(customVenuesMeta.page || 1);
    const customVenuesLimit = Number(customVenuesMeta.limit || 20);
    const customVenuesTotalPages = Number(customVenuesMeta.totalPages || 1);
    const customVenuesTotalItems = Number(customVenuesMeta.total || 0);

    const customVenuesShowingFrom =
        customVenuesTotalItems === 0
            ? 0
            : (customVenuesPage - 1) * customVenuesLimit + 1;

    const customVenuesShowingTo =
        customVenuesTotalItems === 0
            ? 0
            : Math.min(
                  customVenuesPage * customVenuesLimit,
                  customVenuesTotalItems
              );

    return (
        <section className="sa-card lg-analytics-panel">
            <div className="lg-analytics-header">
                <div>
                    <h2>Local Game Analytics Dashboard</h2>
                    <p>
                        Demand, player participation, venue type split, chat health,
                        reminders, and custom venue records.
                    </p>
                </div>

                <div className="lg-analytics-toolbar">
                    <div className="lg-filter-wrap">
                        <button
                            type="button"
                            className="lg-filter-trigger"
                            onClick={() => setFilterOpen((prev) => !prev)}
                            disabled={loading}
                        >
                            <SlidersHorizontal className="lucide" />
                            Filter
                        </button>

                        <div className="lg-filter-summary">
                            {selectedTimeLabel} • {selectedStatusLabel}
                        </div>

                        {filterOpen && (
                            <div className="lg-filter-popover">
                                <div className="lg-filter-popover-head">
                                    <strong>Filter Analytics</strong>
                                    <span>Based on game createdAt date</span>
                                </div>

                                <div className="lg-filter-section">
                                    <div className="lg-filter-section-title">
                                        Time Period
                                    </div>

                                    <div className="lg-filter-options">
                                        {filterOptions.map((option) => {
                                            const active =
                                                selectedFilter === option.value;

                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    className={
                                                        active
                                                            ? "lg-filter-option active"
                                                            : "lg-filter-option"
                                                    }
                                                    onClick={() =>
                                                        onFilterChange?.(option.value)
                                                    }
                                                    disabled={loading}
                                                >
                                                    {option.label}
                                                    {active ? (
                                                        <Check className="lucide" />
                                                    ) : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="lg-filter-section">
                                    <div className="lg-filter-section-title">
                                        Status
                                    </div>

                                    <div className="lg-filter-options">
                                        {statusOptions.map((option) => {
                                            const active =
                                                selectedStatus === option.value;

                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    className={
                                                        active
                                                            ? "lg-filter-option active"
                                                            : "lg-filter-option"
                                                    }
                                                    onClick={() =>
                                                        onStatusChange?.(option.value)
                                                    }
                                                    disabled={loading}
                                                >
                                                    {option.label}
                                                    {active ? (
                                                        <Check className="lucide" />
                                                    ) : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="lg-analytics-actions">
                        <FormButton
                            type="button"
                            variant="secondary"
                            onClick={onRefresh}
                            disabled={loading}
                        >
                            <RefreshCw className="lucide" />
                            Refresh
                        </FormButton>

                        <button
                            type="button"
                            className="lg-analytics-close"
                            onClick={onClose}
                            aria-label="Close analytics"
                        >
                            <X className="lucide" />
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="sa-placeholder" style={{ height: 180 }}>
                    Loading local game analytics…
                </div>
            ) : (
                <>
                    <div className="lg-analytics-grid">
                        <AnalyticsCard
                            label="Total Games Created"
                            value={formatNumber(metrics.totalGames)}
                            note={`${data?.filterLabel || "Selected filter"} • ${data?.statusLabel || "All status"}`}
                        />

                        <AnalyticsCard
                            label="Unique Players Created Games"
                            value={formatNumber(metrics.uniquePlayersCreatedGames)}
                            note="Unique hosts who created games"
                        />

                        <AnalyticsCard
                            label="Active Hosts"
                            value={formatNumber(metrics.activeHostsCount)}
                            note="Hosts with more than 3 games"
                        />

                        <AnalyticsCard
                            label="Joined Players"
                            value={formatNumber(metrics.joinedPlayers)}
                            note="Unique users who clicked join"
                        />

                        <AnalyticsCard
                            label="Open Spots"
                            value={formatNumber(metrics.openSpots)}
                            note="Total player capacity"
                        />

                        <AnalyticsCard
                            label="Filled Spots"
                            value={formatNumber(metrics.filledSpots)}
                            note={`${formatPercent(metrics.fillRate)} fill rate`}
                        />

                        <AnalyticsCard
                            label="Pending Spots"
                            value={formatNumber(metrics.pendingSpots)}
                            note="Remaining empty slots"
                        />

                        <AnalyticsCard
                            label="Success Games"
                            value={formatNumber(metrics.successGames)}
                            note="Games filled to full capacity"
                        />

                        <AnalyticsCard
                            label="Unique Players Actively Joining"
                            value={formatNumber(metrics.uniquePlayersActivelyJoining)}
                            note="Users joined more than 3 games"
                        />
                    </div>

                    <div className="lg-analytics-split lg-analytics-split--two">
                        <div className="lg-analytics-box">
                            <h3>Active Hosts</h3>
                            <p>Hosts who created more than 3 games in the selected filter.</p>

                            {activeHosts.length === 0 ? (
                                <div className="sa-empty">No active hosts found.</div>
                            ) : (
                                activeHosts.map((host) => (
                                    <MiniBar
                                        key={host.userId}
                                        label={`${host.fullName || host.email || host.userId} • ${host.gamesCreated} game(s)`}
                                        value={host.gamesCreated}
                                        max={Math.max(
                                            ...activeHosts.map((item) =>
                                                Number(item.gamesCreated || 0)
                                            ),
                                            0
                                        )}
                                    />
                                ))
                            )}
                        </div>

                        <div className="lg-analytics-box">
                            <h3>Unique Players Actively Joining</h3>
                            <p>Players who joined more than 3 games in the selected filter.</p>

                            {activeJoiningPlayers.length === 0 ? (
                                <div className="sa-empty">No active joining players found.</div>
                            ) : (
                                activeJoiningPlayers.map((player) => (
                                    <MiniBar
                                        key={player.userId}
                                        label={`${player.fullName || player.email || player.userId} • ${player.gamesJoined} joined game(s)`}
                                        value={player.gamesJoined}
                                        max={Math.max(
                                            ...activeJoiningPlayers.map((item) =>
                                                Number(item.gamesJoined || 0)
                                            ),
                                            0
                                        )}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    <div className="lg-analytics-split">
                        <div className="lg-analytics-box">
                            <h3>Venue Type Split</h3>
                            <p>Shows whether users prefer OnGround venues or custom venues.</p>

                            {venueTypeBreakdown.map((item) => (
                                <MiniBar
                                    key={item.label}
                                    label={item.label}
                                    value={item.value}
                                    max={maxVenueType}
                                />
                            ))}
                        </div>

                        <div className="lg-analytics-box">
                            <h3>Status Breakdown</h3>
                            <p>Current lifecycle state of local games.</p>

                            {statusBreakdown.map((item) => (
                                <MiniBar
                                    key={item.label}
                                    label={item.label}
                                    value={item.value}
                                    max={maxStatus}
                                />
                            ))}
                        </div>

                        <div className="lg-analytics-box">
                            <h3>Reminder Health</h3>
                            <p>Tracks 2-hour reminder delivery status.</p>

                            <div className="lg-analytics-mini-grid">
                                <AnalyticsCard
                                    label="Sent"
                                    value={formatNumber(metrics.reminderSent)}
                                />
                                <AnalyticsCard
                                    label="Pending"
                                    value={formatNumber(metrics.reminderPending)}
                                />
                                <AnalyticsCard
                                    label="Failed"
                                    value={formatNumber(metrics.reminderFailed)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="lg-analytics-split lg-analytics-split--two">
                        <div className="lg-analytics-box">
                            <h3>Top Sports Demand</h3>
                            <p>Sports with highest unique user demand.</p>

                            {topSports.length === 0 ? (
                                <div className="sa-empty">No sport data yet.</div>
                            ) : (
                                topSports.map((item) => (
                                    <MiniBar
                                        key={item.sportName}
                                        label={`${item.sportName} • ${formatNumber(item.players)} unique user(s) • ${item.games} game(s) • ${formatPercent(item.fillRate)}`}
                                        value={item.players}
                                        max={maxSportPlayers}
                                    />
                                ))
                            )}
                        </div>

                        <div className="lg-analytics-box">
                            <h3>Last 7 Days Trend</h3>
                            <p>New local games created and player demand by day.</p>

                            {dailyTrend.map((item) => (
                                <MiniBar
                                    key={item.date}
                                    label={`${item.date} • ${formatNumber(item.players)} player(s)`}
                                    value={item.games}
                                    max={maxTrendGames}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="lg-analytics-box lg-analytics-map-box">
                        <div className="lg-analytics-section-head">
                            <div>
                                <h3>Custom Venues</h3>
                                <p>
                                    Venues manually created by users while creating local games.
                                </p>
                            </div>

                            <FormButton
                                type="button"
                                variant="secondary"
                                onClick={onCustomVenueRefresh}
                                disabled={customVenuesLoading}
                            >
                                Refresh
                            </FormButton>
                        </div>

                                                <form
                            className="local-games-filters"
                            onSubmit={onCustomVenueSearchSubmit}
                        >
                            <div className="local-games-search">
                                <Search className="lucide local-games-search-icon" />
                                <FormInput
                                    type="text"
                                    value={customVenuesSearch}
                                    onChange={(event) =>
                                        onCustomVenuesSearchChange?.(
                                            event.target.value
                                        )
                                    }
                                    placeholder="Search by venue name, address, city, or country..."
                                />
                            </div>

                            <FormButton
                                type="submit"
                                variant="secondary"
                                disabled={customVenuesLoading}
                            >
                                Search
                            </FormButton>
                        </form>

                        <div className="sa-table-wrap lg-analytics-table-wrap">
                            <table className="sa-table local-games-table">
                                <thead>
                                    <tr>
                                        <th>S.N.</th>
                                        <th>Venue</th>
                                        <th>Address</th>
                                        <th>City</th>
                                        <th>Country</th>
                                        <th>Coordinates</th>
                                        <th>Created By</th>
                                        <th>Created At</th>
                                        <th>Map</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {customVenuesLoading ? (
                                        <tr>
                                            <td colSpan={9} className="sa-table-empty">
                                                Loading custom venues...
                                            </td>
                                        </tr>
                                    ) : customVenues.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="sa-table-empty">
                                                No custom venues found.
                                            </td>
                                        </tr>
                                    ) : (
                                        customVenues.map((venue, index) => (
                                            <tr key={venue._id}>
                                                <td>
                                                    {(Number(customVenuesMeta.page || 1) - 1) *
                                                        Number(customVenuesMeta.limit || 20) +
                                                        index +
                                                        1}
                                                </td>

                                                <td>
                                                    <strong>{venue.name || "—"}</strong>
                                                </td>

                                                <td>{venue.address || "—"}</td>

                                                <td>{venue.city || "—"}</td>

                                                <td>{venue.country || "—"}</td>

                                                <td>
                                                    <small>
                                                        {venue.latitude || "—"},{" "}
                                                        {venue.longitude || "—"}
                                                    </small>
                                                </td>

                                                <td>
                                                    <strong>
                                                        {venue.createdBy?.fullName || "—"}
                                                    </strong>
                                                    <small className="lg-muted">
                                                        {venue.createdBy?.phoneE164 ||
                                                            venue.createdBy?.email ||
                                                            ""}
                                                    </small>
                                                </td>

                                                <td>
                                                    {venue.createdAt
                                                        ? new Date(
                                                              venue.createdAt
                                                          ).toLocaleString("en-IN", {
                                                              day: "2-digit",
                                                              month: "short",
                                                              year: "numeric",
                                                              hour: "2-digit",
                                                              minute: "2-digit",
                                                          })
                                                        : "—"}
                                                </td>

                                                <td>
                                                    {venue.googleMapsUrl ? (
                                                        <a
                                                            href={venue.googleMapsUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Open
                                                        </a>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="lg-analytics-box lg-analytics-map-box" style={{ marginTop: 24 }}>
    <div className="lg-analytics-section-head">
        <div>
            <h3>Find Players Notification Debug</h3>
            <p>
                Temporary visibility for testing: who was considered,
                selected, sent, failed, or skipped.
            </p>
        </div>
    </div>

    <div className="lg-analytics-mini-grid">
        <AnalyticsCard
            label="Batches Checked"
            value={formatNumber(findPlayersSummary.batches)}
            note="Recent Find Players batches"
        />

        <AnalyticsCard
            label="Users Considered"
            value={formatNumber(findPlayersSummary.nearbyUsers)}
            note="Users considered for notification"
        />

        <AnalyticsCard
            label="Candidates"
            value={formatNumber(findPlayersSummary.candidates)}
            note="Users selected by eligibility"
        />

        <AnalyticsCard
            label="Sent"
            value={formatNumber(findPlayersSummary.sent)}
            note="Push marked as sent"
        />

        <AnalyticsCard
            label="Failed"
            value={formatNumber(findPlayersSummary.failed)}
            note="Push failures"
        />

        <AnalyticsCard
            label="Skipped"
            value={formatNumber(findPlayersSummary.skipped)}
            note="Users not sent"
        />
    </div>

    <div className="lg-analytics-split lg-analytics-split--two">
        <div className="lg-analytics-box">
            <h3>Why Users Were Not Sent</h3>
            <p>Reason breakdown from latest notification debug snapshots.</p>

            {findPlayersReasonBreakdown.length === 0 ? (
                <div className="sa-empty">
                    No Find Players notification debug data yet.
                </div>
            ) : (
                findPlayersReasonBreakdown.map((item) => (
                    <MiniBar
                        key={item.reasonCode}
                        label={item.label || item.reasonCode}
                        value={item.value}
                        max={maxFindPlayersReason}
                    />
                ))
            )}
        </div>

        <div className="lg-analytics-box">
            <h3>Recent Notification Batches</h3>
            <p>Latest Find Players notification batches across selected games.</p>

            <div className="sa-table-wrap lg-analytics-table-wrap">
                <table className="sa-table local-games-table">
                    <thead>
                        <tr>
                            <th>Game</th>
                            <th>Batch</th>
                            <th>Nearby</th>
                            <th>Candidate</th>
                            <th>Sent</th>
                            <th>Failed</th>
                            <th>Skipped</th>
                            <th>Status</th>
                        </tr>
                    </thead>

                    <tbody>
                        {findPlayersRecentBatches.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="sa-table-empty">
                                    No Find Players notification batches found.
                                </td>
                            </tr>
                        ) : (
                            findPlayersRecentBatches.map((batch) => (
                                <tr key={batch._id}>
                                    <td>
                                        <strong>{batch.gameCode || "—"}</strong>
                                        <small className="lg-muted">
                                            {batch.sportName || ""}
                                        </small>
                                    </td>
                                    <td>#{batch.batchNo}</td>
                                    <td>{formatNumber(batch.nearbyUsers)}</td>
                                    <td>{formatNumber(batch.candidates)}</td>
                                    <td>{formatNumber(batch.sent)}</td>
                                    <td>{formatNumber(batch.failed)}</td>
                                    <td>{formatNumber(batch.skipped)}</td>
                                    <td>{batch.status || "—"}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <div className="lg-analytics-box">
        <h3>
            Latest User-Level Decision
            {findPlayersNotifications.latestGameCode
                ? ` • ${findPlayersNotifications.latestGameCode}`
                : ""}
        </h3>
        <p>
            Shows exactly who was selected, who received the push, and why
            others were skipped.
        </p>

        <div className="sa-table-wrap lg-analytics-table-wrap">
            <table className="sa-table local-games-table">
                <thead>
                    <tr>
                        <th>User</th>
                        <th>Phone</th>
                        <th>Distance</th>
                        <th>Candidate</th>
                        <th>Notified</th>
                        <th>Status</th>
                        <th>Reason</th>
                    </tr>
                </thead>

                <tbody>
                    {findPlayersRecentUsers.length === 0 ? (
                        <tr>
                            <td colSpan={7} className="sa-table-empty">
                                No user-level notification debug data yet.
                            </td>
                        </tr>
                    ) : (
                        findPlayersRecentUsers.map((user) => (
                            <tr key={`${user.userId}-${user.reasonCode}`}>
                                <td>
                                    <strong>{user.name || "—"}</strong>
                                    <small className="lg-muted">
                                        {user.email || ""}
                                    </small>
                                </td>
                                <td>{user.phone || "—"}</td>
                                <td>
                                    {user.distanceKm !== null &&
                                    user.distanceKm !== undefined
                                        ? `${user.distanceKm} km`
                                        : "—"}
                                </td>
                                <td>{user.candidate ? "Yes" : "No"}</td>
                                <td>{user.notified ? "Yes" : "No"}</td>
                                <td>{user.status || "—"}</td>
                                <td>
                                    {user.reasonLabel || user.reasonCode || "—"}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    </div>
</div>

                                              <div className="local-games-table-footer">
                            <div className="local-games-result-count">
                                Showing {customVenuesShowingFrom} to{" "}
                                {customVenuesShowingTo} of {customVenuesTotalItems}{" "}
                                results
                            </div>

                            <Paginator
                                page={customVenuesPage}
                                totalPages={customVenuesTotalPages}
                                onPage={onCustomVenuePage}
                                pageSize={customVenuesLimit}
                                onPageSizeChange={onCustomVenuePageSize}
                                pageSizeOptions={[10, 20, 50, 100]}
                            />
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default LocalGameAnalyticsPanel;